import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Clock, Upload, FileText, Filter, Calendar, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { propertiesService } from '@/services/propertiesService';
import complianceService from '@/services/complianceService';

const ComplianceHubPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [complianceMetrics, setComplianceMetrics] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const data = await propertiesService.getProperties();
        setProperties(data || []);
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError('Fehler beim Laden der Immobilien');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchProperties();
    }
  }, [user, authLoading]);

  // Load compliance data when property changes
  useEffect(() => {
    const fetchCompliance = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load deadlines
        const deadlinesData = await complianceService.getDeadlines(selectedProperty);
        setDeadlines(deadlinesData || []);
        
        // Load metrics
        const metricsData = await complianceService.getComplianceMetrics(selectedProperty);
        setComplianceMetrics(metricsData);
        
        console.log('✅ Compliance data loaded:', {
          deadlines: deadlinesData?.length,
          metrics: metricsData
        });
      } catch (err) {
        console.error('❌ Error fetching compliance:', err);
        setError('Fehler beim Laden der Compliance-Daten');
        // Set empty fallbacks
        setDeadlines([]);
        setComplianceMetrics(null);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && user) {
      fetchCompliance();
    }
  }, [user, authLoading, selectedProperty]);

  // Deadlines are already filtered by selectedProperty in the service
  const filteredDeadlines = deadlines;

  // Calculate counts by status
  const counts = {
    overdue: filteredDeadlines.filter(d => d.status === 'OVERDUE').length,
    dueThisMonth: filteredDeadlines.filter(d => d.status === 'DUE_SOON' && d.days_until_due <= 30).length,
    upcoming: filteredDeadlines.filter(d => d.status === 'UPCOMING').length,
    total: filteredDeadlines.length,
  };

  // Get status badge styling
  const getStatusBadge = (status, daysUntilDue) => {
    switch (status) {
      case 'OVERDUE':
        return {
          color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
          icon: <AlertCircle className="w-3 h-3" />,
          text: `${Math.abs(daysUntilDue)} Tage überfällig`,
        };
      case 'DUE_SOON':
        return {
          color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
          icon: <Clock className="w-3 h-3" />,
          text: `In ${daysUntilDue} Tagen fällig`,
        };
      case 'UPCOMING':
        return {
          color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
          icon: <Calendar className="w-3 h-3" />,
          text: `In ${daysUntilDue} Tagen`,
        };
      case 'COMPLETED':
        return {
          color: 'bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-200',
          icon: <CheckCircle2 className="w-3 h-3" />,
          text: 'Erledigt',
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          icon: null,
          text: 'Unbekannt',
        };
    }
  };

  // Format date to German format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Fehler</h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Compliance Hub</h1>
          <p className="text-gray-600 dark:text-gray-400">Betreiberpflichten im Blick – keine Frist verpassen</p>
        </div>
      </div>

      {/* Count Badges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Überfällig</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{counts.overdue}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-red-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Diesen Monat</p>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{counts.dueThisMonth}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Demnächst</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{counts.upcoming}</p>
              </div>
              <Calendar className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gesamt</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{counts.total}</p>
              </div>
              <FileText className="w-10 h-10 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700 dark:text-gray-300">Filter</span>
            </div>
            <div className="flex-1">
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="Immobilie auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Immobilien</SelectItem>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id.toString()}>
                      {property.property_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fristen-Radar Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>🎯 Fristen-Radar</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDeadlines.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Noch keine Pflichten erfasst
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Fügen Sie Betreiberpflichten hinzu, um diese hier zu verfolgen.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDeadlines.map((deadline) => {
                const statusBadge = getStatusBadge(deadline.status, deadline.days_until_due);
                
                return (
                  <div
                    key={deadline.id}
                    className={`p-4 rounded-lg border-l-4 ${
                      deadline.status === 'OVERDUE'
                        ? 'border-l-red-500 bg-red-50 dark:bg-red-900/10'
                        : deadline.status === 'DUE_SOON'
                        ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10'
                        : 'border-l-green-500 bg-green-50 dark:bg-green-900/10'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Left: Obligation Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {deadline.obligation_title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Building2 className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {deadline.property_name}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {deadline.obligation_category}
                              </span>
                            </div>
                          </div>
                          <Badge className={`${statusBadge.color} flex items-center gap-1`}>
                            {statusBadge.icon}
                            {statusBadge.text}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <span>
                            <strong>Fällig:</strong> {formatDate(deadline.due_date)}
                          </span>
                          <span>
                            <strong>Intervall:</strong> alle {deadline.interval_months} Monate
                          </span>
                          <span>
                            <strong>Verantwortlich:</strong> {deadline.responsible_party}
                          </span>
                        </div>

                        {deadline.last_completed && (
                          <div className="text-sm text-gray-500 dark:text-gray-500">
                            Zuletzt erledigt: {formatDate(deadline.last_completed)}
                          </div>
                        )}
                      </div>

                      {/* Right: Quick Actions */}
                      <div className="flex flex-row lg:flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 lg:flex-none"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Erledigt
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 lg:flex-none"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Nachweis
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                🚧 MVP-Phase: Mock-Daten aktiv
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Diese Ansicht zeigt Beispieldaten. Nach der Datenbank-Anbindung werden hier Ihre echten Betreiberpflichten angezeigt.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceHubPage;
